import EnvConfig from "../config/env.config";
import { ClientStorage, ClientStorageItem } from "../utils/clientstorage";
import { handleUnauthorizedResponse } from "../utils/handle.unauthorized";

const prepareArray = (arr: unknown[]): unknown[] => {
  return arr.map(prepareVal).filter((v) => v !== null);
};

const prepareVal = (val: unknown) => {
  if (!val) {
    if (val === 0 || val === false) return val;
  } else {
    if (typeof val === "object") {
      if (Array.isArray(val)) {
        return prepareArray(val);
      } else if (val !== null)
        return prepareObject(val as Record<string, unknown>);
    } else return val;
  }
  return null;
};

const prepareObject = (obj: Record<string, unknown>) => {
  const res: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(obj)) {
    const valRes = prepareVal(val);
    if (valRes !== null) res[key] = valRes;
  }
  return res;
};

const convertObject = (obj: Record<string, unknown>) => {
  const params = new URLSearchParams();
  for (const [key, val] of Object.entries(obj)) params.set(key, String(val));
  return params.toString();
};

export const __internallyPrepareRequest = ({
  baseRoute,
  ext,
  params,
  query,
}: Partial<BASE_API_REQ>) =>
  (baseRoute || EnvConfig.apiBaseUrl) +
  (ext ? `/${ext}` : "") +
  (params ? `/${params}` : "") +
  (query
    ? `?${convertObject(prepareObject(query as Record<string, unknown>))}`
    : "");

export type BASE_API_REQ = {
  baseRoute: string; // doesn't end with /
  ext: string;
  params: string;
  query: Record<string, string | number | undefined>;
  body: FormData | string | Record<string, unknown>;
  token: string;
  headers: Record<string, string>;
};

export type ResponseType<T> = {
  statusCode: number;
  message: string;
  payload: T;
  error: string | null;
  token: string | null;
};

const REQ_METHOD = async <T>(
  reqMethod: "POST" | "GET" | "PUT" | "DELETE",
  baseApiReq: Partial<BASE_API_REQ>,
  signal?: AbortSignal,
): Promise<ResponseType<T>> => {
  const { body, headers } = baseApiReq;
  const reqUrl = __internallyPrepareRequest(baseApiReq);
  const bodyIsOriginallyStringOrFormData =
    typeof body === "string" || body instanceof FormData;
  const reqBody = body
    ? bodyIsOriginallyStringOrFormData
      ? body
      : JSON.stringify(prepareObject(body as Record<string, unknown>))
    : undefined;

  const accessToken = ClientStorage.fetch(ClientStorageItem.AccessToken);
  const XAccountHash = ClientStorage.fetch(ClientStorageItem.XAccountHash);
  const weddingVersion = ClientStorage.fetch(ClientStorageItem.WeddingVersion);

  const sentHeaders = {
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(XAccountHash ? { "X-Account-Hash": XAccountHash } : {}),
    ...(reqMethod !== "GET" && weddingVersion
      ? { "If-Match": weddingVersion }
      : {}),
    ...(!bodyIsOriginallyStringOrFormData
      ? { "Content-Type": "application/json" }
      : {}),
    ...headers,
  };

  try {
    const res = await fetch(reqUrl, {
      method: reqMethod,
      credentials: "include",
      headers: sentHeaders,
      ...(body ? { body: reqBody } : {}),
      ...(signal ? { signal } : {}),
    });

    // Safe JSON parsing to handle empty responses (204, etc.)
    let data: ResponseType<T>;
    try {
      const text = await res.text();
      if (text) {
        data = JSON.parse(text) as ResponseType<T>;
      } else {
        // Empty response - create success response
        data = {
          statusCode: res.status,
          message: "Success",
          payload: {} as T,
          error: null,
          token: null,
        };
      }
    } catch (jsonError) {
      // Malformed JSON - treat as error
      throw new Error("Invalid response format");
    }

    handleUnauthorizedResponse(res.status, data.message);

    const responseVersion = (data.payload as { version?: unknown })?.version;
    if (typeof responseVersion === "number")
      ClientStorage.store(
        ClientStorageItem.WeddingVersion,
        String(responseVersion),
      );
    return { ...data, statusCode: res.status || data.statusCode };
  } catch (error: unknown) {
    return {
      statusCode: 0,
      message: "Unable to reach the server",
      payload: {} as T,
      error: error instanceof Error ? error.message : "Unknown error",
      token: null,
    };
  }
};

export const POST = <T>(
  baseApiReq: Partial<BASE_API_REQ>,
  signal?: AbortSignal,
) => REQ_METHOD<T>("POST", baseApiReq, signal);
export const PUT = <T>(
  baseApiReq: Partial<BASE_API_REQ>,
  signal?: AbortSignal,
) => REQ_METHOD<T>("PUT", baseApiReq, signal);
export const DELETE = <T>(
  baseApiReq: Partial<BASE_API_REQ>,
  signal?: AbortSignal,
) => REQ_METHOD<T>("DELETE", baseApiReq, signal);
export const GET = <T>(
  baseApiReq: Partial<BASE_API_REQ>,
  signal?: AbortSignal,
) => REQ_METHOD<T>("GET", baseApiReq, signal);
